import moment from "moment";
import React, { memo } from "react";
import { Field, Form } from "react-final-form";

import { InfoRounded as InfoIcon } from "@material-ui/icons";
import ExlyCheckbox from "common/form/ExlyCheckbox";
import { ExlyDropdown, ExlyInput, ExlyOfferingSelect } from "common/form";
import ExlyPriceInput from "common/form/ExlyMoneyInput";
import { DurationPickerField, TooltipTitle } from "./components/CustomFields";
import LimitedOfferShare from "../../modules/LimitedOfferTimeShare";
import { is_desktop } from "utils/Utils";
import constants from "../../../constants/constants";
import {
  getUserCurrencySymbol,
  isInternationalCurrency,
  isInternationalTimezone,
} from "../../../utils/AuthUtil";
import { useStyles } from "./index.styles";
import style from "./Style.module.css";
import { useLimitedTimeOffer } from "./useLimitedTimeOffer";
import {
  filterTriggersByServer,
  getServerSyncDate,
  getServerThresholdUnits,
  useBatchOptions,
  useOfferTriggerConfig,
} from "./useOfferApis";
import { required } from "common/validate";
import { composeValidators } from "utils/validations";
import { getUserTimezone } from "utils/AuthUtil";
import timezones from "constants/timezones/AllTimezonesArray";
import {
  validateOfferName,
  validateDiscountPrice,
  validatePercentage,
  validateEndDate,
  validateDiscountPriceInternational,
} from "./limitedOfferValidators";
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT_12,
} from "constants/dateTime";
import { ExlyNote } from "common/Components/ExlyNote/ExlyNote";
import { isInstallmentsPlanType } from "schedule-v2/commonPages/Components/PaymentPlanWithInstallments/utils/PaymentPlanWithInstallments.utils";
import { PART_PAYMENT_FORM_KEYS } from "schedule-v2/commonPages/Components/PaymentPlanWithInstallments/PaymentPlanWithInstallments.data";
import {
  LTO_FORM_KEYS,
  OFFER_TARGET_TYPE,
  OFFER_TARGET_TYPE_OPTIONS,
  OFFER_TYPE,
  OFFER_TYPE_OPTIONS,
  TIMER_TYPE,
  TIMER_TYPE_OPTIONS,
  DURATION_UNITS,
  DEFAULT_THRESHOLD_UNIT,
  VALUE_UNIT,
} from "./constants";
import { validateUnifiedConfig } from "./utils/validateUnifiedConfig";
import { getOfferLockState } from "./utils/offerLockState";
import CustomCodeSelect from "ui/pages/CustomCodes/components/HelperComponents/CustomCodeSelect";
import { TierBuilderTable } from "./components/TierBuilderTable";
import { TimeProximityTiers } from "./components/TimeProximityTiers";
import { VipSlotsBuilder } from "./components/VipSlotsBuilder";
import { BatchWiseTable } from "./components/BatchWiseTable";
import {
  OFFER_DIRECTION,
  TRIGGER_TYPE,
  TRIGGER_LABELS,
  TRIGGER_TIER_META,
  DEFAULT_TRIGGER_BY_FAMILY,
  getTriggerOptionsForTarget,
  getOfferingStartDate,
  inferOfferingFamily,
  isUnscheduledOffering,
  isTieredTrigger,
  isTimeProximityTrigger,
  withSavedTrigger,
} from "./utils/unifiedOffer";
import { asArray, emptyTimeTier } from "./utils/tierOps";
import { useFetchCustomCodeComboPage } from "ui/pages/CustomCodes/hooks/useFetchCustomCodeComboPage";
import { ComboPageOption } from "ui/pages/CustomCodes/components/HelperComponents/CombosPageOption";
import EmptySalesPageView from "ui/pages/CustomCodes/components/HelperComponents/EmptySalesPageView";
import { checkIsArrayEmpty } from "@my-scoot/exly-react-component-lib";
import { useInfiniteListingOptions } from "features/Listings/utils/useInfiniteListingOptions";
import { LINK_TYPES } from "features/PaymentLinks/modules/CreatePaymentLinkModal/constants/CreatePaymentLinkModal.constants";

export const getGSTHelperText = (gst_status, gst_percentage) => {
  const gst_factor = 1 + (gst_percentage ?? 18) / 100;
  return [
    {
      text: "Price shown to the customer will be",
      factor: gst_status === constants.GST_STATUS.INCLUSIVE ? 1 : gst_factor,
    },
    {
      text: "Price excluding your GST will be",
      factor:
        gst_status === constants.GST_STATUS.INCLUSIVE ? 1 / gst_factor : 1,
    },
  ];
};

/**
 * Form-level validation for the unified offer config.
 *
 * Array-level rules (≥1 row, ascending/descending, overlap, batch default) are
 * returned under `*_error` keys, NEVER under the array path itself. final-form's
 * processErrors writes every registered field via `setIn(errors, name, value)`;
 * a scalar at `tiers` while nested fields `tiers[0].x` exist throws
 * "Cannot set a numeric property on an object". A `tiers_error` key has no
 * nested siblings, so it is always safe and still blocks submit.
 */
export { validateUnifiedConfig } from "./utils/validateUnifiedConfig";

export const FormContent = ({
  handleSubmit,
  form,
  values,
  isEdit,
  recordData,
  DateField,
  timeZone,
  placeholder,
  setPlaceHolder,
  offeringOptions,
  dynamicPagesOptions,
  shouldShowOfferingSelector,
  shouldShowDynamicLinkSelector,
  shouldShowPriceFields,
  shouldShowInternationalPricing,
  loadingInfiniteListings,
  lastElementInfiniteListingsRef,
  allDynamicPagesLoader,
  lastElementDynamicPagesRef,
  hideLayout,
  classes,
  gst_status,
  props,
  initialValues,
}) => {
  // Self-heal: final-form can leave a row array as a numeric-keyed object after
  // nested fields unregister, which makes later nested changes throw. Force the
  // active builder's row list back to an array whenever it isn't one.
  React.useEffect(() => {
    const trigger = values?.[LTO_FORM_KEYS.TRIGGER_TYPE];
    const keysToNormalize = [];
    if (isTieredTrigger(trigger) || isTimeProximityTrigger(trigger)) {
      keysToNormalize.push(LTO_FORM_KEYS.TIERS);
    }
    if (trigger === TRIGGER_TYPE.VIP_SLOTS) {
      keysToNormalize.push(LTO_FORM_KEYS.VIP_WINDOWS);
    }
    if (trigger === TRIGGER_TYPE.BATCH_WISE) {
      keysToNormalize.push(LTO_FORM_KEYS.BATCH_VALUES);
    }
    keysToNormalize.forEach((key) => {
      if (!Array.isArray(values?.[key])) {
        form.change(key, asArray(values?.[key]));
      }
    });
  }, [
    values?.[LTO_FORM_KEYS.TRIGGER_TYPE],
    values?.[LTO_FORM_KEYS.TIERS],
    values?.[LTO_FORM_KEYS.VIP_WINDOWS],
    values?.[LTO_FORM_KEYS.BATCH_VALUES],
    form,
  ]);

  React.useEffect(() => {
    const dynamicPageCurrencies = [
      ...new Set(
        (values?.[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS] || [])
          .map((id) =>
            dynamicPagesOptions.find(
              (p) => p.dynamic_link_id === id || p.value === id
            )
          )
          .filter(Boolean)
          .map((p) => p.currency)
          .filter(Boolean)
      ),
    ];
    const hasMixedCurrencies = dynamicPageCurrencies.length > 1;
    const isAllOfferings =
      values?.target_type === OFFER_TARGET_TYPE.ALL_OFFERINGS;
    const shouldRestrictToPercentage = hasMixedCurrencies || isAllOfferings;

    if (
      shouldRestrictToPercentage &&
      values[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.AMOUNT
    ) {
      form.change(LTO_FORM_KEYS.OFFER_TYPE, OFFER_TYPE.PERCENTAGE);
    }
  }, [
    values?.[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS],
    values?.target_type,
    values?.[LTO_FORM_KEYS.OFFER_TYPE],
    dynamicPagesOptions,
    form,
  ]);

  const {
    [LTO_FORM_KEYS.LISTINGS]: selectedListings,
    [LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS]: selectedDynamicLinkUuids,
  } = values;

  const firstListing = offeringOptions.find(
    (o) => o.uuid === selectedListings?.[0]
  );
  const firstDynamicPageId = selectedDynamicLinkUuids?.[0];
  const firstDynamicPage = firstDynamicPageId
    ? dynamicPagesOptions.find((p) => p.dynamic_link_id === firstDynamicPageId)
    : null;

  const regionForCurrency = isEdit
    ? recordData?.region ?? values?.region
    : values?.region;
  const currencySymbol = isEdit
    ? getUserCurrencySymbol(
        regionForCurrency === constants.domestic_region &&
          !isInternationalCurrency()
          ? constants.inr_currency
          : constants.usd_currency
      ) ?? constants.inr_currency
    : (firstDynamicPage?.currency
        ? constants.CURRENCY_SYMBOL_MAP?.[firstDynamicPage?.currency]
        : firstListing?.currency
        ? constants.CURRENCY_SYMBOL_MAP?.[firstListing?.currency]
        : getUserCurrencySymbol(
            regionForCurrency === constants.domestic_region &&
              !isInternationalCurrency()
              ? constants.inr_currency
              : constants.usd_currency
          )) ?? constants.inr_currency;

  const selectedDynamicPages = selectedDynamicLinkUuids?.length
    ? dynamicPagesOptions.filter(
        (p) =>
          selectedDynamicLinkUuids.includes(p.value) ||
          selectedDynamicLinkUuids.includes(p.dynamic_link_id)
      )
    : [];

  const selectedOfferings = selectedListings?.length
    ? offeringOptions.filter((o) => selectedListings.includes(o.uuid))
    : [];

  // Batch Wise needs real batch (class-group) uuids — listing uuids are
  // rejected by the backend. classes[] comes from creator listing details.
  const batchListingUuid =
    selectedOfferings.length === 1 ? selectedOfferings[0]?.uuid : null;
  const { batches: batchClassOptions, isLoading: batchesLoading } =
    useBatchOptions(batchListingUuid);
  const batchTableOptions =
    batchClassOptions.length > 0 || batchesLoading
      ? batchClassOptions
      : selectedOfferings;

  const originalInrPrice =
    values?.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING
      ? Math.max(
          ...selectedOfferings.map((o) => o.price_per_head).filter(Boolean),
          0
        )
      : values?.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE
      ? Math.max(
          ...selectedDynamicPages.map((p) => p.link_price).filter(Boolean),
          0
        )
      : null;

  const originalUsdPrice =
    values?.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING
      ? Math.max(
          ...selectedOfferings
            .map((o) => o.price_international)
            .filter(Boolean),
          0
        )
      : null;

  // International pricing is only offerable when every selected listing
  // carries its own international base price — the backend validates offer
  // values against it and 406s ("Price for international users not set
  // for ...") otherwise.
  const allListingsHaveIntlPrice =
    selectedOfferings.length > 0 &&
    selectedOfferings.every((o) => Number(o.price_international) > 0);

  const dynamicPageCurrencies = [
    ...new Set(selectedDynamicPages.map((p) => p.currency).filter(Boolean)),
  ];
  const hasMixedCurrencies = dynamicPageCurrencies.length > 1;
  const isAllOfferings =
    values?.target_type === OFFER_TARGET_TYPE.ALL_OFFERINGS;
  const shouldRestrictToPercentage = hasMixedCurrencies || isAllOfferings;

  const handleTargetTypeChange = (e) => {
    form.reset({
      ...initialValues,
      [LTO_FORM_KEYS.TARGET_TYPE]: e,
    });
    setPlaceHolder(null);
  };

  // ---- Unified offer derived state (frontend-only, additive) ----
  const direction =
    values?.[LTO_FORM_KEYS.DIRECTION] || OFFER_DIRECTION.DISCOUNT;
  const isSurge = direction === OFFER_DIRECTION.SURGE;
  const bookingCount = values?.bookingCount ?? 0;
  const { isLocked: fullyLocked, structuralDisabled } = getOfferLockState({
    isEdit,
    bookingCount,
    editState: recordData?.edit_state || values?.edit_state || null,
  });
  const isUniversalTarget =
    values?.target_type === OFFER_TARGET_TYPE.ALL_OFFERINGS;
  const isPaymentPageTarget =
    values?.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE;
  // Trigger Type is only offered once a target is chosen; Universal shows all,
  // specific offerings filter by family, payment pages show the common two.
  // Server trigger/config then intersects as the source of truth (§5/§6).
  const localAvailableTriggers = React.useMemo(
    () =>
      getTriggerOptionsForTarget({
        isUniversal: isUniversalTarget,
        isPaymentPage: isPaymentPageTarget,
        hasPaymentPages: (selectedDynamicLinkUuids || []).length > 0,
        selectedOfferings,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isUniversalTarget,
      isPaymentPageTarget,
      JSON.stringify((selectedDynamicLinkUuids || []).map((d) => String(d))),
      JSON.stringify((selectedOfferings || []).map((o) => o.uuid || o.value)),
    ]
  );
  const { data: serverTriggerConfig } = useOfferTriggerConfig({
    listingUids: selectedListings,
    targetType: values?.target_type,
  });
  const availableTriggers = React.useMemo(() => {
    const filtered = filterTriggersByServer(
      localAvailableTriggers,
      serverTriggerConfig
    );
    // Never drop an existing offer's saved trigger: the server allow-list
    // can omit it for the listing (then the locked dropdown renders empty
    // and the update drifts → 400). Creates always pick from valid options.
    return withSavedTrigger(
      filtered,
      values?.[LTO_FORM_KEYS.TRIGGER_TYPE],
      isEdit
    );
  }, [
    localAvailableTriggers,
    serverTriggerConfig,
    isEdit,
    values?.[LTO_FORM_KEYS.TRIGGER_TYPE],
  ]);
  const serverSyncDate = getServerSyncDate(serverTriggerConfig);
  const canConfigureTrigger = availableTriggers.length > 0;
  const triggerOptions = availableTriggers.map((t) => ({
    label: TRIGGER_LABELS[t] || t,
    value: t,
  }));
  const activeTrigger =
    values?.[LTO_FORM_KEYS.TRIGGER_TYPE] || TRIGGER_TYPE.SCHEDULE;
  const tierMeta = TRIGGER_TIER_META[activeTrigger] || {};
  const showTiered = isTieredTrigger(activeTrigger);
  const showTimeTiers = isTimeProximityTrigger(activeTrigger);
  const showVip = activeTrigger === TRIGGER_TYPE.VIP_SLOTS;
  const showBatchWise = activeTrigger === TRIGGER_TYPE.BATCH_WISE;
  const showSingleValue =
    !showTiered && !showVip && !showBatchWise && !showTimeTiers;
  const isCountdownTrigger = !!tierMeta.countdown;
  // Clear stale config when trigger changes (never hidden-but-retained).
  // If the server allows fewer threshold units than currently selected
  // (e.g. count-only without capacity), snap to the first allowed unit so
  // the payload can never carry a unit the backend rejects.
  React.useEffect(() => {
    if (!isTieredTrigger(activeTrigger)) return;
    const serverUnits = getServerThresholdUnits(
      serverTriggerConfig,
      activeTrigger
    );
    const current = values?.[LTO_FORM_KEYS.THRESHOLD_UNIT];
    if (serverUnits && current && !serverUnits.includes(current)) {
      form.change(LTO_FORM_KEYS.THRESHOLD_UNIT, serverUnits[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverTriggerConfig, activeTrigger]);
  // value_unit has no UI (hidden Field in builders) — Offer Type is the single
  // source of truth: Percentage → percent, Amount → flat. Keeps the backend
  // payload (threshold_unit, value_unit, tiers) exactly the same.
  React.useEffect(() => {
    const expected =
      values?.[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.PERCENTAGE
        ? VALUE_UNIT.PERCENT
        : VALUE_UNIT.FLAT;
    if (values?.[LTO_FORM_KEYS.VALUE_UNIT] !== expected) {
      form.change(LTO_FORM_KEYS.VALUE_UNIT, expected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.[LTO_FORM_KEYS.OFFER_TYPE]]);
  const handleTriggerChange = (next) => {
    form.change(LTO_FORM_KEYS.TRIGGER_TYPE, next);
    // Keep the legacy timer key in sync: only the Evergreen trigger is
    // evergreen; every other trigger (schedule, count/time tiers, vip, batch)
    // is fixed — otherwise a stale value leaks evergreen fields into the
    // payload for non-evergreen triggers.
    form.change(
      LTO_FORM_KEYS.TIMER_TYPE,
      next === TRIGGER_TYPE.EVERGREEN ? TIMER_TYPE.EVERGREEN : TIMER_TYPE.FIXED
    );
    // Mockup defaults: countdown triggers read absolute counts, the rest %.
    // Server allowed-units win when known (e.g. count-only without capacity).
    const meta = TRIGGER_TIER_META[next] || {};
    const serverUnits = getServerThresholdUnits(serverTriggerConfig, next);
    form.change(
      LTO_FORM_KEYS.THRESHOLD_UNIT,
      (serverUnits && serverUnits[0]) ||
        meta.defaultThresholdUnit ||
        DEFAULT_THRESHOLD_UNIT
    );
    // value_unit follows Offer Type (no UI) — keep payload in sync on reset.
    form.change(
      LTO_FORM_KEYS.VALUE_UNIT,
      values?.[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.PERCENTAGE
        ? VALUE_UNIT.PERCENT
        : VALUE_UNIT.FLAT
    );
    form.change(LTO_FORM_KEYS.TIERS, [emptyTimeTier()]);
    form.change(LTO_FORM_KEYS.VIP_WINDOWS, []);
    form.change(LTO_FORM_KEYS.VIP_START_DATE, "");
    form.change(LTO_FORM_KEYS.VIP_END_DATE, "");
    form.change(LTO_FORM_KEYS.BATCH_VALUES, []);
    form.change(LTO_FORM_KEYS.SYNC_END_TO_START, false);
  };
  const showTimeBasedConfig =
    !showTiered && !showTimeTiers && !showVip && !showBatchWise;
  const showEvergreenConfig =
    showTimeBasedConfig && activeTrigger === TRIGGER_TYPE.EVERGREEN;
  // Offering start for sync-to-start (§4.3). Checkbox hidden without one (§6).
  const offeringStartDate = React.useMemo(
    () => getOfferingStartDate(selectedOfferings),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify((selectedOfferings || []).map((o) => o.uuid || o.value))]
  );
  const isEndDateSynced = !!values?.[LTO_FORM_KEYS.SYNC_END_TO_START];
  // Server sync date backs the local offering-start lookup (§4.3).
  // Server sends ISO datetime; only the calendar date fills end_date.
  const syncStartDate =
    offeringStartDate ||
    (/^\d{4}-\d{2}-\d{2}/.test(serverSyncDate || "")
      ? serverSyncDate.slice(0, 10)
      : null);
  // Sync row needs a schedulable offering AND a resolved start date.
  // QPP (Quick Payment Page / no_schedule) never qualifies — even if a
  // datetime leaks in from the listing or trigger config. Empty selection
  // (payment-page target) keeps today's server-date-backed behavior.
  const hasSchedulableOffering =
    selectedOfferings.length === 0 ||
    selectedOfferings.some((o) => !isUnscheduledOffering(o));
  const showSyncRow = !!syncStartDate && hasSchedulableOffering;
  const handleSyncEndDateChange = (checked) => {
    form.change(LTO_FORM_KEYS.SYNC_END_TO_START, checked);
    if (checked && syncStartDate) {
      form.change("end_date", syncStartDate);
      form.change("end_date_international", syncStartDate);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={style.limitedContainer}>
        {!hideLayout && (
          <div className={style.limitedTitleContent}>
            <div className={style.limitedTitle}>
              {isEdit
                ? `Edit ${isSurge ? "Surge Pricing Rule" : "Early Bird Offer"}`
                : isSurge
                ? "Create Surge Pricing Rule"
                : "Create Early Bird Offer"}
            </div>
            <div
              className={style.crossIcon}
              onClick={() => props.handleClick()}
            >
              &times;
            </div>
          </div>
        )}

        <div className={classes.offerfeildContainer}>
          <Field
            name={LTO_FORM_KEYS.TARGET_TYPE}
            component={ExlyDropdown}
            fullWidth
            size="small"
            label="Where do you want to run this offer?"
            disabled={isEdit}
            placeholder="Select target type"
            options={OFFER_TARGET_TYPE_OPTIONS}
            validate={(value) => !value && "Please select a target type"}
            onChange={handleTargetTypeChange}
          />

          {shouldShowOfferingSelector(values?.target_type) && (
            <Field
              name={LTO_FORM_KEYS.LISTINGS}
              component={ExlyOfferingSelect}
              label="Select Offerings *"
              mobileModalTitle="Select Offerings"
              placeholder="Select Offerings"
              showStatus
              fullWidth={true}
              multiple
              disabled={isEdit}
              onChange={(value) => {
                const ids = Array.isArray(value) ? value : [value];
                const first = offeringOptions.find((o) => o.uuid === ids[0]);
                if (first?.price_per_head) {
                  setPlaceHolder(
                    `Eg, ${Math.round(
                      first.price_per_head - first.price_per_head * 0.25
                    )} (original price - 25%)`
                  );
                }
                // Reset incompatible trigger → family default (§5/§6).
                const nextSelected = offeringOptions.filter((o) =>
                  ids.includes(o.uuid)
                );
                const nextTriggers = getTriggerOptionsForTarget({
                  selectedOfferings: nextSelected,
                });
                if (!nextTriggers.includes(activeTrigger)) {
                  const nextFams = [
                    ...new Set(
                      nextSelected.map(inferOfferingFamily).filter(Boolean)
                    ),
                  ];
                  const familyDefault =
                    nextFams.length === 1
                      ? DEFAULT_TRIGGER_BY_FAMILY[nextFams[0]]
                      : null;
                  handleTriggerChange(
                    familyDefault && nextTriggers.includes(familyDefault)
                      ? familyDefault
                      : TRIGGER_TYPE.SCHEDULE
                  );
                }
                // Synced end date belongs to the previous offering — re-sync.
                form.change(LTO_FORM_KEYS.SYNC_END_TO_START, false);
              }}
              validate={(value) =>
                !value?.length && "Please select at least one offering"
              }
              options={offeringOptions}
              lastElementRef={lastElementInfiniteListingsRef}
              loading={loadingInfiniteListings}
            />
          )}

          {shouldShowDynamicLinkSelector(values?.target_type) && (
            <Field
              name={LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS}
              component={CustomCodeSelect}
              fullWidth
              size="small"
              options={dynamicPagesOptions}
              disabled={isEdit}
              loading={allDynamicPagesLoader}
              showStatus={true}
              multiple
              showChips
              label="Select Payment Pages"
              mobileModalTitle="Select Payment Pages"
              placeholder="Select Payment Pages"
              renderOption={(renderProps) => (
                <ComboPageOption
                  showCheckbox
                  showStatus
                  lastElementRef={lastElementDynamicPagesRef}
                  {...renderProps}
                />
              )}
              headerComponent={
                checkIsArrayEmpty(dynamicPagesOptions) &&
                !allDynamicPagesLoader && <EmptySalesPageView />
              }
              validate={(value) =>
                !value?.length
                  ? "Please select at least one payment page"
                  : undefined
              }
            />
          )}

          {values?.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING &&
            selectedListings?.[0] &&
            isInstallmentsPlanType(
              offeringOptions.find((o) => o.uuid === selectedListings[0])?.[
                PART_PAYMENT_FORM_KEYS.payment_plan_type
              ]
            ) && (
              <ExlyNote
                className="m-0"
                label={
                  <div>
                    <strong>Please note:</strong> This is a Part-payment enabled
                    offering. Limited-time offer will apply to the one-time
                    payment option only, not the part payment.
                  </div>
                }
              />
            )}

          <Field
            component={ExlyInput}
            labelClassName={classes.inputLabel}
            name="offerName"
            label="Offer Name *"
            placeholder="Eg, Flash Deal"
            fullWidth
            disabled={fullyLocked}
            validate={validateOfferName}
          />

          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            {/* Direction is fixed by the Step-0 chooser — not switchable here. */}
            <span
              className={`tw-rounded-full tw-px-3 tw-py-1 tw-text-[12px] tw-font-semibold ${
                isSurge
                  ? "tw-bg-orange-100 tw-text-orange-800"
                  : "tw-bg-green-100 tw-text-green-800"
              }`}
            >
              {isSurge ? "Surge — price increases" : "Early Bird — discount"}
            </span>
          </div>

          <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-p-2">
            <Field
              id="showAtCheckout"
              name={LTO_FORM_KEYS.SHOW_AT_CHECKOUT}
              type="checkbox"
              component={ExlyCheckbox}
              size="large"
              disabled={fullyLocked}
              onChange={(checked) =>
                form.change(LTO_FORM_KEYS.SHOW_AT_CHECKOUT, checked)
              }
            />
            <label htmlFor="showAtCheckout" className="tw-text-[13px]">
              <span className="tw-font-medium">
                Show this offer to the customer at checkout
              </span>
              <span className="tw-block tw-text-[12px] tw-text-gray-500">
                {values?.[LTO_FORM_KEYS.SHOW_AT_CHECKOUT]
                  ? "Customer sees timer / badge / progress + price line."
                  : "Silent — price is already adjusted, no indicator or line item."}
              </span>
            </label>
          </div>

          {isEdit && !fullyLocked && (
            <div className="tw-rounded-lg tw-border tw-border-solid tw-border-indigo-300 tw-bg-indigo-50 tw-p-2.5 tw-text-[12px] tw-text-gray-600">
              Editing this offer — 0 bookings so far. You can update the{" "}
              <strong>Offer Name</strong> and <strong>values</strong> below. The
              offering and trigger type are locked once an offer is created — to
              change those, create a new offer instead.
            </div>
          )}
          {isEdit && fullyLocked && (
            <div className="tw-rounded-lg tw-border tw-border-solid tw-border-red-300 tw-bg-red-50 tw-p-2.5 tw-text-[12px] tw-text-red-700">
              This offer already has bookings and can no longer be edited.
              Deactivate it from the offers list if you need to stop it.
            </div>
          )}

          <Field
            name={LTO_FORM_KEYS.OFFER_TYPE}
            component={ExlyDropdown}
            fullWidth
            size="small"
            label="Offer Type"
            placeholder="Select offer type"
            options={OFFER_TYPE_OPTIONS}
            validate={(value) => !value && "Please select offer type"}
            // Offer type is structural (amount ↔ percentage changes what the
            // backend stores) — locked once created, like offering/trigger.
            // Values below stay editable per the edit banner.
            disabled={shouldRestrictToPercentage || fullyLocked || isEdit}
          />

          {showSingleValue && shouldShowPriceFields(values) && (
            <>
              {values[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.PERCENTAGE ? (
                <Field
                  name={LTO_FORM_KEYS.PERCENTAGE_VALUE}
                  component={ExlyInput}
                  label={
                    <TooltipTitle
                      title="Offer Value"
                      description="Set the discount percentage for this offer"
                      className="!tw-mb-2"
                    />
                  }
                  type="number"
                  placeholder="Eg, 20"
                  fullWidth
                  suffix="%"
                  disabled={fullyLocked}
                  validate={(value) => validatePercentage(value, { isSurge })}
                />
              ) : (
                <Field
                  name={LTO_FORM_KEYS.INR_PRICE}
                  component={ExlyPriceInput}
                  label={
                    <TooltipTitle
                      title="Offer Value"
                      description="Set the new price you want the customers to pay for this offer"
                      className="!tw-mb-2"
                    />
                  }
                  type="number"
                  placeholder={placeholder}
                  fullWidth
                  symbol={currencySymbol}
                  disabled={fullyLocked}
                  validate={(value) =>
                    validateDiscountPrice(value, originalInrPrice, { isSurge })
                  }
                />
              )}

              {values[LTO_FORM_KEYS.OFFER_TYPE] !== OFFER_TYPE.PERCENTAGE &&
              values[LTO_FORM_KEYS.INR_PRICE] > 0 &&
              values.region === constants.domestic_region &&
              form.getFieldState(LTO_FORM_KEYS.INR_PRICE)?.valid &&
              gst_status !== constants.GST_STATUS.INAPPLICABLE &&
              !isInternationalTimezone() ? (
                <div className={classes.gstInfo}>
                  <InfoIcon />
                  <div className={classes.infoContainer}>
                    {getGSTHelperText(
                      gst_status,
                      offeringOptions.find(
                        (o) => o.uuid === selectedListings?.[0]
                      )?.metadata?.gst_percent
                    ).map((item) => (
                      <div key={item.text}>
                        {item.text}&nbsp;
                        {currencySymbol}
                        {Math.round(values.INRPrice * item.factor * 100) / 100}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
          {showSingleValue &&
            shouldShowPriceFields(values) &&
            values[LTO_FORM_KEYS.OFFER_TYPE] !== OFFER_TYPE.PERCENTAGE &&
            shouldShowInternationalPricing(values) &&
            allListingsHaveIntlPrice && (
              <>
                <div className={classes.salescheckbox}>
                  <Field
                    id="USDCheckbox"
                    name={LTO_FORM_KEYS.USD_CHECKBOX}
                    type="checkbox"
                    component={ExlyCheckbox}
                    size="large"
                    disabled={fullyLocked}
                    onChange={(checked) =>
                      form.change(LTO_FORM_KEYS.USD_CHECKBOX, checked)
                    }
                  />
                  <label
                    htmlFor="USDCheckbox"
                    className={classes.checkboxtitle}
                  >
                    I want to set a different price for International clients
                  </label>
                </div>

                {values[LTO_FORM_KEYS.USD_CHECKBOX] && (
                  <>
                    <Field
                      name={LTO_FORM_KEYS.USD_PRICE}
                      component={ExlyPriceInput}
                      placeholder="Enter price"
                      type="number"
                      fullWidth
                      symbol="$"
                      disabled={fullyLocked}
                      validate={(value) =>
                        validateDiscountPriceInternational(
                          value,
                          originalUsdPrice,
                          { isSurge }
                        )
                      }
                    />

                    {values[LTO_FORM_KEYS.USD_PRICE] > 0 &&
                    form.getFieldState(LTO_FORM_KEYS.USD_PRICE)?.valid &&
                    gst_status !== constants.GST_STATUS.INAPPLICABLE ? (
                      <div className={classes.gstInfo}>
                        <InfoIcon />
                        <div className={classes.infoContainer}>
                          {getGSTHelperText(gst_status).map((item) => (
                            <div key={item.text}>
                              {item.text}&nbsp;
                              {constants.usd_currency}
                              {Math.round(
                                values[LTO_FORM_KEYS.USD_PRICE] *
                                  item.factor *
                                  100
                              ) / 100}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </>
            )}

          {canConfigureTrigger && (
            <>
              <TooltipTitle
                title="Trigger Type"
                description="Time is one kind of trigger among several. Options filter by the offering(s) selected above."
              />
              <Field
                name={LTO_FORM_KEYS.TRIGGER_TYPE}
                component={ExlyDropdown}
                fullWidth
                size="small"
                placeholder="Select trigger type"
                options={triggerOptions}
                disabled={isEdit}
                validate={(value) => !value && "Please select trigger type"}
                onChange={handleTriggerChange}
              />

              {/* Legacy timer key follows trigger for schedule/evergreen parity */}
              <div style={{ display: "none" }}>
                <Field
                  name={LTO_FORM_KEYS.TIMER_TYPE}
                  component={ExlyDropdown}
                  fullWidth
                  size="small"
                  placeholder="Select timer type"
                  options={TIMER_TYPE_OPTIONS}
                  validate={(value) => !value && "Please select timer type"}
                />
              </div>

              {/* Amount offers with a price table: opt-in to a different
                  price for international clients. The checkbox toggles the
                  inline "International ($)" column in the table below. */}
              {(showTiered || showTimeTiers || showVip || showBatchWise) &&
                values[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.AMOUNT &&
                ((shouldShowInternationalPricing(values) &&
                  allListingsHaveIntlPrice) ||
                  (isEdit && !!values[LTO_FORM_KEYS.USD_CHECKBOX])) && (
                  <div className={classes.salescheckbox}>
                    <Field
                      id="USDCheckboxTiers"
                      name={LTO_FORM_KEYS.USD_CHECKBOX}
                      type="checkbox"
                      component={ExlyCheckbox}
                      size="large"
                      disabled={fullyLocked}
                      onChange={(checked) =>
                        form.change(LTO_FORM_KEYS.USD_CHECKBOX, checked)
                      }
                    />
                    <label
                      htmlFor="USDCheckboxTiers"
                      className={classes.checkboxtitle}
                    >
                      I want to set a different price for International clients
                    </label>
                  </div>
                )}

              {showTiered && (
                <TierBuilderTable
                  values={values}
                  form={form}
                  disabled={fullyLocked}
                  structuralDisabled={structuralDisabled}
                  metricLabel={tierMeta.metricLabel || "THRESHOLD"}
                  thresholdPhrasing={
                    isCountdownTrigger
                      ? "drops to or below"
                      : "reaches or exceeds"
                  }
                  numberSuffix={tierMeta.numberSuffix || "#"}
                  showInternational={
                    values[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.AMOUNT &&
                    !!values[LTO_FORM_KEYS.USD_CHECKBOX]
                  }
                  allowPercentThreshold={(() => {
                    const serverUnits = getServerThresholdUnits(
                      serverTriggerConfig,
                      activeTrigger
                    );
                    if (serverUnits) return serverUnits.includes("percent");
                    return !tierMeta.absoluteOnly;
                  })()}
                  originalPrice={
                    selectedOfferings.length === 1 ? originalInrPrice : null
                  }
                />
              )}

              {showTimeTiers && (
                <TimeProximityTiers
                  values={values}
                  form={form}
                  metricLabel={tierMeta.metricLabel || "TIME BEFORE START"}
                  showInternational={
                    values[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.AMOUNT &&
                    !!values[LTO_FORM_KEYS.USD_CHECKBOX]
                  }
                  disabled={fullyLocked}
                  structuralDisabled={structuralDisabled}
                  originalPrice={
                    selectedOfferings.length === 1 ? originalInrPrice : null
                  }
                />
              )}

              {showVip && (
                <VipSlotsBuilder
                  values={values}
                  form={form}
                  showInternational={
                    values[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.AMOUNT &&
                    !!values[LTO_FORM_KEYS.USD_CHECKBOX]
                  }
                  disabled={fullyLocked}
                  structuralDisabled={structuralDisabled}
                  originalPrice={originalInrPrice}
                />
              )}

              {showBatchWise && (
                <BatchWiseTable
                  values={values}
                  form={form}
                  showInternational={
                    values[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.AMOUNT &&
                    !!values[LTO_FORM_KEYS.USD_CHECKBOX]
                  }
                  disabled={fullyLocked}
                  structuralDisabled={structuralDisabled}
                  batchOptions={batchTableOptions}
                  originalPrice={originalInrPrice}
                />
              )}

              {showEvergreenConfig ? (
                <>
                  {/* "Starts counting from" is intentionally hidden: evergreen
                      always counts from the customer's first page view and the
                      default enum is still sent with the payload. */}
                  <TooltipTitle
                    title="Offer Duration"
                    description={
                      isSurge
                        ? "Surge pricing will be applied after this offer duration, counted from when a customer first visits the page"
                        : "Set how long the offer timer will count down from when a customer first visits the page"
                    }
                  />
                  <div className="tw-flex tw-w-full tw-gap-3">
                    <div className="tw-flex-1 tw-min-w-0">
                      <Field
                        name={LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE}
                        component={ExlyInput}
                        type="number"
                        min="1"
                        label="Duration *"
                        placeholder="Enter duration"
                        fullWidth
                        disabled={fullyLocked}
                        validate={(value) => !value && "Please enter duration"}
                      />
                    </div>
                    <div className="tw-shrink-0 tw-w-[130px] -tw-mt-[0.2rem]">
                      <Field
                        name={LTO_FORM_KEYS.EVERGREEN_DURATION_UNIT}
                        component={ExlyDropdown}
                        options={DURATION_UNITS}
                        fullWidth
                        size="small"
                        label="Unit"
                        disabled={fullyLocked}
                      />
                    </div>
                  </div>
                </>
              ) : showTimeBasedConfig ? (
                <>
                  <TooltipTitle
                    title="Discount Period"
                    description="Select a time period for which the discount will be valid for all customers"
                  />

                  {/* Sync row only for offerings with a scheduled start
                      (slot-type/scheduled offerings). Dateless offerings and
                      QPP (no_schedule) never see it — even if a datetime
                      leaks in. Date fields below stay for all schedule
                      offers (still required at submit). */}
                  {showSyncRow ? (
                    <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-p-2 tw-mb-2">
                      <Field
                        id="syncEndToStart"
                        name={LTO_FORM_KEYS.SYNC_END_TO_START}
                        type="checkbox"
                        component={ExlyCheckbox}
                        size="large"
                        disabled={fullyLocked}
                        onChange={(checked) => handleSyncEndDateChange(checked)}
                      />
                      <label
                        htmlFor="syncEndToStart"
                        className="tw-text-[13px]"
                      >
                        <span className="tw-font-medium">
                          Sync end date to this offering&apos;s start date
                        </span>
                        <span className="tw-block tw-text-[12px] tw-text-gray-500">
                          {`Auto-fills from the offering (${syncStartDate}) and locks manual edit.`}
                        </span>
                      </label>
                    </div>
                  ) : null}

                  <DurationPickerField
                    label="Offer Start Date *"
                    datePickerProps={{
                      name: "start_date",
                      disabled: fullyLocked,
                      validate: (value) => !value && "Required",
                      parse: (date) => {
                        const formattedDate = moment(new Date(date)).format(
                          "YYYY-MM-DD"
                        );
                        // In edit (non-draft) skip the today-time auto-set and
                        // intl mirror side effects — but still return the
                        // picked date. Returning undefined here wiped the
                        // field, failing "Required" on every date change.
                        if (isEdit && recordData.status !== 0)
                          return formattedDate;

                        const todaysDate = new Date();

                        if (
                          date?.getDate() === todaysDate?.getDate() &&
                          date?.getFullYear() === todaysDate?.getFullYear() &&
                          date?.getMonth() === todaysDate?.getMonth()
                        ) {
                          const time = DateField(todaysDate, "fetchTime");
                          form.change("start_time", time?.time.toLowerCase());
                          form.change(
                            "start_time_international",
                            time?.time.toLowerCase()
                          );
                        }

                        values?.[LTO_FORM_KEYS.LISTINGS]?.length > 0 &&
                          form.change(
                            "start_date_international",
                            formattedDate
                          );

                        return formattedDate;
                      },
                    }}
                    timePickerProps={{
                      name: "start_time",
                      disabled: fullyLocked,
                      parse: (value) => {
                        form.change("start_time_international", value);
                        return value;
                      },
                      uniqueId: `offer_start_time`,
                      validate: composeValidators(required, (value) => {
                        // Live offers start in the past — requiring a future
                        // start here would block every edit save with no
                        // toast (silent validation block). Backend owns it.
                        if (isEdit) return undefined;
                        const { start_date } = values;
                        const isPast = moment(
                          `${start_date} ${value}`,
                          `${DEFAULT_DATE_FORMAT} ${DEFAULT_TIME_FORMAT_12}`
                        ).isBefore(moment());
                        if (isPast) return "Please pick time in future";
                      }),
                    }}
                  />

                  <DurationPickerField
                    label="Offer End Date *"
                    datePickerProps={{
                      name: "end_date",
                      disabled: isEndDateSynced || fullyLocked,
                      validate: (value) => validateEndDate(value, values),
                      parse: (value) => {
                        const formattedDate = moment(new Date(value)).format(
                          "YYYY-MM-DD"
                        );

                        values?.[LTO_FORM_KEYS.LISTINGS]?.length > 0 &&
                          form.change("end_date_international", formattedDate);

                        return formattedDate;
                      },
                    }}
                    timePickerProps={{
                      name: "end_time",
                      disabled: fullyLocked,
                      uniqueId: `offer_end_time`,
                      parse: (value) => {
                        form.change("end_time_international", value);
                        return value;
                      },
                    }}
                  />
                  <div className={style.termsText}>
                    <p>{`** Default Timezone is ${timeZone}`}</p>
                  </div>
                  {fullyLocked && (
                    <p className="tw-text-[12px] tw-text-gray-500">
                      Locked — this offer has bookings. Deactivate it from the
                      list instead of editing.
                    </p>
                  )}
                </>
              ) : null}
            </>
          )}
        </div>

        {!hideLayout && (
          <div className={style.offerButton}>
            <button type="submit" className={style.createButton}>
              {isEdit ? "Update Offer" : "Create Offer"}
            </button>
            {!isEdit && (
              <button className={style.clearButton} onClick={form.reset}>
                Clear
              </button>
            )}
          </div>
        )}
      </div>
    </form>
  );
};

export const LimitedOfferModalContent = memo(
  React.forwardRef((props, ref) => {
    const classes = useStyles({ is_desktop });

    const { showShareModal = true, forceShowForm = false } = props;

    const {
      data: allDynamicPagesData,
      loading: allDynamicPagesLoader,
      lastElementRef: lastElementDynamicPagesRef,
    } = useFetchCustomCodeComboPage({
      initialData: {
        isActive: true,
      },
      paymentLinkType: [LINK_TYPES.CUSTOM_LINK, LINK_TYPES.COMBO_LINK],
    });

    const {
      options: infiniteListingOptions,
      loading: loadingInfiniteListings,
      lastElementRef: lastElementInfiniteListingsRef,
    } = useInfiniteListingOptions({
      formatOptions: (options) =>
        options?.map((listing) => ({
          ...listing,
          id: listing.uuid,
          label: listing.title,
          value: listing.uuid,
        })),
      isOfferingVariantsEnabled: false,
      hideMerchVariantsEnabledListing: true,
    });

    const {
      isEdit,
      initialValues,
      recordData,
      firstLimitedOffer,
      listingUuid,
      limitedOfferCreative,
      handleCreateOffer,
      DateField,
      selectedListing,
      shareUrl,
      shouldShowOfferingSelector,
      shouldShowDynamicLinkSelector,
      shouldShowPriceFields,
      shouldShowInternationalPricing,
      dynamicPagesOptions,
      offeringOptions,
    } = useLimitedTimeOffer({
      allDynamicPagesData,
      infiniteListingOptions,
      ...props,
    });

    const timeZone = timezones.all_timezones.find(
      (o) => o.value === getUserTimezone()
    ).label;

    const { gst_status, hideLayout } = props;
    const [placeholder, setPlaceHolder] = React.useState(null);

    const formRef = React.useRef(null);

    React.useImperativeHandle(ref, () => ({
      triggerSave() {
        if (!formRef.current) return;

        const { submit } = formRef.current;
        const { values: formState, valid } = formRef.current.getState();

        submit();
        return valid ? handleCreateOffer(formState).then((res) => res) : {};
      },
    }));

    return (
      <>
        {forceShowForm || !limitedOfferCreative ? (
          <Form
            initialValues={initialValues}
            enableReinitialize
            validate={validateUnifiedConfig}
            onSubmit={!hideLayout ? handleCreateOffer : () => {}}
          >
            {({ handleSubmit, values, form }) => {
              formRef.current = form;
              return (
                <FormContent
                  handleSubmit={handleSubmit}
                  form={form}
                  values={values}
                  isEdit={isEdit}
                  recordData={recordData}
                  handleCreateOffer={handleCreateOffer}
                  DateField={DateField}
                  timeZone={timeZone}
                  placeholder={placeholder}
                  setPlaceHolder={setPlaceHolder}
                  offeringOptions={offeringOptions}
                  dynamicPagesOptions={dynamicPagesOptions}
                  shouldShowOfferingSelector={shouldShowOfferingSelector}
                  shouldShowDynamicLinkSelector={shouldShowDynamicLinkSelector}
                  shouldShowPriceFields={shouldShowPriceFields}
                  shouldShowInternationalPricing={
                    shouldShowInternationalPricing
                  }
                  loadingInfiniteListings={loadingInfiniteListings}
                  lastElementInfiniteListingsRef={
                    lastElementInfiniteListingsRef
                  }
                  allDynamicPagesLoader={allDynamicPagesLoader}
                  lastElementDynamicPagesRef={lastElementDynamicPagesRef}
                  hideLayout={hideLayout}
                  classes={classes}
                  gst_status={gst_status}
                  props={props}
                  initialValues={initialValues}
                />
              );
            }}
          </Form>
        ) : (
          <LimitedOfferShare
            setShowModal={limitedOfferCreative && showShareModal}
            firstOffer={firstLimitedOffer}
            listing={selectedListing}
            uid={listingUuid}
            shareUrl={shareUrl}
            showBroadcast
          />
        )}
      </>
    );
  })
);
